import React from "react";
import { Link } from "react-router-dom";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, Loader2, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";

export default function StaffPendientesRevision() {
  const { user } = useAuth();
  const isSuper = user?.roles?.includes("superadmin_dcc");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/programs/pending-review");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <StaffLayout user={user} superadmin={isSuper}>
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">Editorial</p>
        <h1 className="mt-1 text-3xl font-bold text-white font-display">Programas en revisión</h1>
        <p className="mt-1 text-sm text-gray-400">Aprueba o rechaza propuestas de instructores para el catálogo público.</p>
      </div>

      {loading ? (
        <div className="text-sm text-gray-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-10 text-center text-sm text-gray-400" data-testid="pending-review-empty">
          <Clock className="w-8 h-8 text-gray-500 mx-auto" />
          <p className="mt-3">Nada pendiente. Bien hecho.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_400px] gap-4">
          <div className="grid gap-3">
            {items.map((p) => (
              <button key={p.id} onClick={() => setSelected(p)}
                      data-testid={`pending-row-${p.id}`}
                      className={`w-full text-left flex items-start justify-between gap-4 rounded-2xl border p-4 hover:bg-white/[0.08] ${selected?.id === p.id ? "border-[#F26A21] bg-white/[0.06]" : "border-white/10 bg-white/5"}`}>
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-widest text-gray-500">{p.code} · {p.format}</p>
                  <p className="mt-1 text-sm font-semibold text-white">{p.title}</p>
                  <p className="mt-1 text-xs text-gray-400 line-clamp-2">{p.description}</p>
                  <p className="mt-2 text-[11px] text-gray-500">
                    Por {p.created_by_name || "—"} · {p.modules_count} módulo(s)
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0 mt-2" />
              </button>
            ))}
          </div>
          <div>
            {selected ? (
              <ReviewPanel key={selected.id} program={selected} onDone={() => { setSelected(null); load(); }} />
            ) : (
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center text-sm text-gray-400">
                Selecciona un programa para revisarlo.
              </div>
            )}
          </div>
        </div>
      )}
    </StaffLayout>
  );
}

function ReviewPanel({ program, onDone }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const decide = async (approve) => {
    if (!approve && !notes.trim()) {
      toast.error("Notas obligatorias al rechazar.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/programs/${program.id}/review`, { approve, notes });
      toast.success(approve ? "Programa aprobado y publicado." : "Programa rechazado.");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="review-panel">
      <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">Revisar</p>
      <h3 className="mt-1 text-lg font-bold text-white">{program.title}</h3>
      <p className="mt-1 text-xs text-gray-400">{program.description}</p>
      <div className="mt-3 space-y-1 text-xs text-gray-300">
        <p><span className="text-gray-500">Formato:</span> {program.format}</p>
        <p><span className="text-gray-500">Duración:</span> {program.duration_text}</p>
        <p><span className="text-gray-500">Audiencia:</span> {program.target_audience || "—"}</p>
        <p><span className="text-gray-500">Módulos:</span> {program.modules_count}</p>
      </div>

      <Link to={`/app/staff/programas/${program.id}/preview`} className="mt-3 inline-block text-xs text-[#F26A21] hover:underline">
        Ver estructura completa →
      </Link>

      <div className="mt-4">
        <label className="text-xs font-semibold text-gray-300">Notas</label>
        <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
                  data-testid="review-notes-input"
                  className="mt-1 w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white"
                  placeholder="Retroalimentación editorial (obligatorio si rechazas)…" />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button onClick={() => decide(false)} disabled={busy}
                data-testid="review-reject-btn"
                className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-md border border-red-500/40 text-red-300 text-sm hover:bg-red-500/10 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} Rechazar
        </button>
        <button onClick={() => decide(true)} disabled={busy}
                data-testid="review-approve-btn"
                className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-md bg-emerald-500 text-[#0B0F14] font-semibold text-sm hover:brightness-110 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Aprobar
        </button>
      </div>
    </div>
  );
}
